import { useState } from "react";
import { Outlet, createRootRouteWithContext, useRouter } from "@tanstack/react-router";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { authStore } from "@/features/auth/store/authStore";
import { AuthUnavailableError } from "@/features/auth/authErrors";
import Error from "@/components/Error";

interface MyRouterContext {
  auth: typeof authStore;
  isMobile: boolean;
}

const RootError = ({ error, reset }: ErrorComponentProps) => {
  const router = useRouter();
  const [isRetrying, setIsRetrying] = useState(false);

  if (!(error instanceof AuthUnavailableError)) {
    return <Error status={500} error={error} reset={reset} />;
  }

  const handleRetry = async () => {
    setIsRetrying(true);
    const auth = await authStore.initialize();
    if (auth.status !== "unavailable") {
      reset();
      await router.invalidate();
    }
    setIsRetrying(false);
  };

  return (
    <Error
      variant="unavailable"
      error={error}
      reset={reset}
      isRetrying={isRetrying}
      onRetry={() => void handleRetry()}
    />
  );
};

export const Route = createRootRouteWithContext<MyRouterContext>()({
  component: () => (
    <>
      <Outlet />
    </>
  ),
  notFoundComponent: () => <Error status={404} />,
  errorComponent: RootError,
});
